import * as Sentry from '@sentry/browser';
import { useQueryClient } from '@tanstack/react-query';
import { LoaderCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { GetPartyByIdResponse } from 'wg-api-sdk';

import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Button } from '@/components/shadcn/button';
import toPartyAddress from '@/features/party/components/CreatePartyModal/toPartyAddress';
import type { CreatePartyValues } from '@/features/party/components/CreatePartyModal/types';
import useUpdateParty from '@/features/party/mutations/useUpdateParty';
import useParty from '@/features/party/queries/hooks/useParty';
import useAuth from '@/hooks/useAuth';
import hasItems from '@/lib/utils/hasItems';
import queryKeys from '@/queries/queryKeys';

import useExportParty from '../../hooks/useExportParty';
import type { CRM } from '../../types';

import ExportPartyForm, { type ExportPartyTitleKey } from './ExportPartyForm';

type Props = ModalProps & {
  partyId: string;
  crms: Array<{ key: string; name?: string }>;
  titleKey: ExportPartyTitleKey;
  onExported?: (party: GetPartyByIdResponse) => void;
};

function toPartyChanges(values: CreatePartyValues) {
  const email = values.email?.trim() || undefined;
  const address = toPartyAddress(values.address);

  if (values.entityType === 'LEGAL') {
    return { email, address, name: values.companyName };
  }

  return {
    email,
    address,
    first_name: values.firstName,
    last_name: values.lastName,
    initials: values.initials,
    birth: values.birthdate || undefined,
  };
}

export default function ExportPartyModal({
  partyId,
  crms,
  titleKey,
  onExported,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { distribution } = useAuth();

  const { data: party, refetch: refetchParty } = useParty(partyId);
  const { mutateAsync: updateParty } = useUpdateParty();
  const { exportParty } = useExportParty();

  const formatCrmNames = (crmKeys: Array<CRM>) => {
    const names = crmKeys.map(
      (key) => crms.find((crm) => crm.key === key)?.name ?? key,
    );

    return new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
      names,
    );
  };

  const onSubmit = async (
    values: CreatePartyValues,
    { hasPartyChanges }: { hasPartyChanges: boolean },
  ) => {
    if (hasPartyChanges) {
      try {
        await updateParty({ partyId, values: toPartyChanges(values) });
      } catch (error) {
        toast.error(t('EXPORT_PARTY_UPDATE_FAILED'));
        Sentry.captureException(error);

        return;
      }
    }

    const selectedCrms = Object.entries(values.exportToCrm)
      .filter(([, shouldExport]) => shouldExport)
      .map(([crm]) => crm as CRM);

    const { failedCrms, errorMessageByCrm } = await exportParty(
      partyId,
      selectedCrms,
    );

    const exportedCrms = selectedCrms.filter(
      (crm) => !failedCrms.includes(crm),
    );

    failedCrms.forEach((crm) =>
      toast.error(
        t('PARTY_CREATE_INLINE_EXPORT_FAILED', { crms: formatCrmNames([crm]) }),
        {
          description: errorMessageByCrm[crm],
          duration: Infinity,
          closeButton: true,
        },
      ),
    );

    if (!hasItems(exportedCrms)) return;

    toast.success(
      t('EXPORT_PARTY_SUCCESS', { crms: formatCrmNames(exportedCrms) }),
    );

    queryClient.invalidateQueries({
      queryKey: queryKeys.party.lists(distribution?.id ?? ''),
    });
    const { data: exportedParty } = await refetchParty();

    if (onExported && exportedParty) {
      onExported(exportedParty);

      return;
    }

    onClose();
  };

  if (!party) {
    return (
      <div className="relative flex min-w-full items-center justify-center p-8">
        <Button
          onClick={onClose}
          className="absolute top-2 right-2"
          variant="ghost"
          size="icon-xs"
        >
          <X />
        </Button>

        <LoaderCircle className="text-primary-500 size-6 animate-spin" />
      </div>
    );
  }

  return (
    <ExportPartyForm
      party={party}
      crms={crms}
      titleKey={titleKey}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  );
}
